import { useEffect, useRef, forwardRef, useImperativeHandle, ElementType, Ref } from 'react';
import {
  Box,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import LocalPostOfficeIcon from '@mui/icons-material/LocalPostOffice';
import { useLanguage } from '../../../../context/LanguageContext';
import { ChannelType } from './CommunicationDefinitionStep';
import EmailUnlayerSection, { EmailUnlayerHandle } from './EmailUnlayerSection';
import SmsCreationSection from './SmsCreationSection';
import TikeoCreationSection from './TikeoCreationSection';
import CashdeskCreationSection from './CashdeskCreationSection';
import CourrierCreationSection from './CourrierCreationSection';

interface CommunicationCreationStepProps {
  channel?: ChannelType;
  onHasContent?: (v: boolean) => void;
  offerCount?: number;
  offerTitles?: string[];
  onAddOffer?: () => void;
  onOfferUsed?: (offerIndex: number) => void;
  onOfferIndicesChange?: (indices: Set<number>) => void;
  initialPayload?: Record<string, unknown> | null;
  onPayloadChange?: (payload: Record<string, unknown>) => void;
}

export interface CreationStepHandle {
  getEmailContent: () => Promise<{ mode: 'unlayer' | 'html'; html: string; design?: object; subject: string } | null>;
}

const CHANNEL_META: Record<string, { label: string; Icon: ElementType; color: string }> = {
  courrier: { label: 'Courrier',      Icon: LocalPostOfficeIcon,         color: '#fd7e14' },
  email:    { label: 'Email',         Icon: EmailIcon,                   color: '#0d6efd' },
  sms:      { label: 'SMS',           Icon: SmsIcon,                     color: '#6f42c1' },
  wallet:   { label: 'Wallet',        Icon: AccountBalanceWalletIcon,    color: '#20c997' },
  cashdesk: { label: 'Caisse',        Icon: PointOfSaleIcon,             color: '#e83e8c' },
};

const CommunicationCreationStep = forwardRef((props: CommunicationCreationStepProps, ref: Ref<CreationStepHandle>) => {
  const { channel, onHasContent, offerCount = 1, offerTitles, onAddOffer, onOfferUsed, onOfferIndicesChange, initialPayload, onPayloadChange } = props;
  const { t } = useLanguage();
  const emailRef = useRef<EmailUnlayerHandle>(null);

  const meta = channel ? CHANNEL_META[channel] : null;

  useEffect(() => {
    if (channel && channel !== 'email') onHasContent?.(true);
  }, [channel]);

  useImperativeHandle(ref, () => ({
    getEmailContent: () => emailRef.current?.getContent() ?? Promise.resolve(null),
  }), []);

  const renderContent = () => {
    switch (channel) {
      case 'email':
        return (
          <EmailUnlayerSection
            ref={emailRef}
            onSave={() => onHasContent?.(true)}
            offerCount={offerCount}
            offerTitles={offerTitles}
            onAddOffer={onAddOffer}
            onOfferUsed={onOfferUsed}
            onOfferIndicesChange={onOfferIndicesChange}
          />
        );
      case 'sms':
        return (
          <SmsCreationSection
            offerCount={offerCount}
            offerTitles={offerTitles}
            onAddOffer={onAddOffer}
            onOfferUsed={onOfferUsed}
            initialPayload={initialPayload}
            onPayloadChange={onPayloadChange}
          />
        );
      case 'wallet':
        return <TikeoCreationSection />;
      case 'courrier':
        return <CourrierCreationSection />;
      case 'cashdesk':
        return (
          <CashdeskCreationSection
            offerCount={offerCount}
            onAddOffer={onAddOffer}
            onOfferUsed={onOfferUsed}
            onOfferIndicesChange={onOfferIndicesChange}
          />
        );
      default:
        return (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Veuillez d'abord sélectionner un canal à l'étape <strong>Définition</strong>.
          </Alert>
        );
    }
  };

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('pages.marketing.communication.creation')}
        </Typography>
        {meta && (
          <Chip
            icon={<meta.Icon sx={{ fontSize: '0.9rem !important' }} />}
            label={meta.label}
            size="small"
            sx={{
              backgroundColor: `${meta.color}18`,
              color: meta.color,
              border: `1px solid ${meta.color}40`,
              fontWeight: 600,
              fontSize: '0.75rem',
              '& .MuiChip-icon': { color: meta.color },
            }}
          />
        )}
      </Box>

      {renderContent()}
    </Box>
  );
});

CommunicationCreationStep.displayName = 'CommunicationCreationStep';

export default CommunicationCreationStep;
